import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  Validators,
  FormBuilder,
  FormGroup,
  AbstractControl,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BlogService } from '../../../services/blog.service';
import constants from '../../../utility/constants';
import { environment } from '../../../../environments/environment';
import { AppValidators } from '../../../core/validators/app-validators';
import { ImageValidators } from '../../../core/validators/image-validators';
import messages from '../../../utility/messages';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-add-blog',
  templateUrl: './add-blog.component.html',
  styleUrls: ['./add-blog.component.css'],
})
export class AddBlogComponent implements OnInit, OnDestroy {
  blogForm: FormGroup;
  submitted: boolean = false;
  actionText: string = 'Add New Post';
  blogId: number = 0;
  showView: boolean = false;

  blogContent: string = '';

  //images
  blogSize: string = constants.admin.uploadSetting.blogMaxSize;
  blogFormat: string = constants.admin.uploadSetting.blogType;
  reader = new FileReader();
  imagePreviewUrl: string;

  // url
  serverUrl: string = environment.serverUrl;

  returnUrl: string = '../';
  isPublishLoader: boolean = false;

  fieldMaxLengthMed: number = constants.common.validations.maxLength200;
  private subscriptions: Array<Subscription> = [];

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private blogService: BlogService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.initiateBlogForm();

    this.route.params.subscribe((params) => {
      this.blogId = +params['id'];

      if (this.blogId > 0) {
        this.returnUrl = '../../';
        this.bindBlogDetails(this.blogId);
      } else {
        this.blogId = 0;
        this.showView = true;
      }
    });

    this.reader.onload = () => {
      this.imagePreviewUrl = this.reader.result as string;
    };
  }

  private initiateBlogForm() {
    this.blogForm = this.formBuilder.group({
      title: [
        '',
        [
          Validators.required,
          AppValidators.noWhitespace,
          Validators.maxLength(this.fieldMaxLengthMed),
        ],
      ],
      author: [
        '',
        [
          Validators.required,
          Validators.maxLength(this.fieldMaxLengthMed),
          AppValidators.noWhitespace,
        ],
      ],
      coverImage: [
        null,
        [
          Validators.required,
          ImageValidators.sizeCheck(this.blogSize),
          ImageValidators.imageFormat(this.blogFormat),
        ],
      ],
      content: [null, [Validators.required]],
    });
  }

  editorContent(
    event: { data: string; limitReached: boolean },
    control: AbstractControl
  ) {
    control.patchValue(event.data);
    control.markAsDirty();
    if (event.limitReached) control.setErrors({ invalid: true });
    else control.setErrors(null);
  }

  onFileChanged(files: FileList) {
    let control = this.blogForm.controls.coverImage;
    if (files.length > 0) {
      this.blogForm.patchValue({ coverImage: files[0] });
      control.markAsTouched();
      control.updateValueAndValidity();
      if (control.valid) {
        this.reader.readAsDataURL(files[0]);
      } else {
        this.imagePreviewUrl = null;
        control.markAsTouched();
      }
    }
  }

  private bindBlogDetails(id: number) {
    this.actionText = 'Edit Post';
    const req = this.blogService.getBlog(id).subscribe(
      (response) => {
        if (
          (response.draftTitle != null && response.draftContent != null) ||
          response.draftAuthor != null ||
          response.draftCoverImagePath != null
        ) {
          this.blogContent = response.draftContent;

          response.draftCoverImagePath
            ? (this.imagePreviewUrl = response.draftCoverImagePath)
            : null;

          this.blogForm.patchValue({
            title: response.draftTitle,
            author: response.draftAuthor,
            content: response.draftContent,
            coverImage: response.draftCoverImagePath,
          });
        } else {
          // if blog is not drafted
          this.blogContent = response.content;

          response.coverImagePath
            ? (this.imagePreviewUrl = response.coverImagePath)
            : null;

          this.blogForm.patchValue({
            title: response.title,
            author: response.author,
            content: response.content,
            coverImage: response.coverImagePath,
          });
        }
        this.showView = true;
      },
      (err) => {
        this.toastr.error(
          err,
          constants.common.toast.errorTitle,
          constants.common.toast.config
        );
        this.onCancel();
      }
    );
    this.subscriptions.push(req);
  }

  onSubmit(isPublish: boolean = true) {
    this.submitted = true;

    if (this.blogForm.invalid) {
      return;
    }

    if (isPublish) {
      this.isPublishLoader = !this.isPublishLoader;
    }

    let formValues = this.blogForm.value;

    let modelFormData = new FormData();
    modelFormData.append('title', formValues.title);
    modelFormData.append('author', formValues.author);
    modelFormData.append('content', formValues.content);
    (typeof formValues.coverImage === 'string')? 
    modelFormData.append('coverImage', formValues.coverImage) : 
    modelFormData.append('coverImage', formValues.coverImage, formValues.coverImage?.name);
    modelFormData.append('saveAsDraft', '' + !isPublish);

    if (this.blogId > 0) {
      modelFormData.append('id', '' + this.blogId);
      const req1 = this.blogService.updateBlog(modelFormData).subscribe(
        () => {
          this.submitted = false;
          this.isPublishLoader = !this.isPublishLoader;
          this.toastr.success(
            messages.addBlog.updated,
            constants.common.toast.successTitle,
            constants.common.toast.config
          );
          this.onCancel();
        },
        (err) => {
          this.submitted = false;
          this.isPublishLoader = !this.isPublishLoader;
          this.toastr.error(
            err,
            constants.common.toast.errorTitle,
            constants.common.toast.config
          );
        }
      );
      this.subscriptions.push(req1);
    } else {
      const req2 = this.blogService.addBlog(modelFormData).subscribe(
        () => {
          this.submitted = false;
          this.isPublishLoader = !this.isPublishLoader;
          this.toastr.success(
            messages.addBlog.added,
            constants.common.toast.successTitle,
            constants.common.toast.config
          );
          this.onCancel();
        },
        (err) => {
          this.submitted = false;
          this.isPublishLoader = !this.isPublishLoader;
          this.toastr.error(
            err,
            constants.common.toast.errorTitle,
            constants.common.toast.config
          );
        }
      );
      this.subscriptions.push(req2);
    }
  }

  onCancel() {
    this.blogForm.reset();
    this.router.navigate([this.returnUrl], { relativeTo: this.route });
  }
  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
  }
}
